/**
 * ResetPasswordPage — magic-link landing for the security email flow.
 *
 * Two entry paths land here:
 *   1) `/reset-password?token=…&from=secure_lock` — after the user clicked
 *      "Lock my account + reset password" in a security email. By the time
 *      they arrive every session for their account has already been
 *      destroyed server-side and the token is one-use.
 *   2) `/reset-password?token=…` — from a vanilla forgot-password email.
 *
 * UI is intentionally a one-page form: new password + confirm + submit →
 * redirects to login on success.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShieldCheck, Eye, EyeOff, AlertTriangle, Lock } from "lucide-react";
import { API, formatApiError } from "../contexts/AuthContext";
import "../lms.css";
import "../portal.css";

export default function ResetPasswordPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const token = params.get("token");
  const fromSecureLock = params.get("from") === "secure_lock";

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  // Auto-bounce if there's no token in the URL.
  useEffect(() => {
    if (!token) {
      const t = setTimeout(() => navigate("/login", { replace: true }), 3500);
      return () => clearTimeout(t);
    }
  }, [token, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) { setErr("Use at least 8 characters."); return; }
    if (pw !== pw2)   { setErr("Passwords don't match."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      setDone(true);
      // Bounce to login after a beat so the success state is visible.
      setTimeout(() => navigate("/login?reset=1", { replace: true }), 1800);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || "This reset link is invalid or has expired. Request a new one.");
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6"
           style={{ background: "var(--px-bg)" }}>
        <div className="lms-card p-8 max-w-md text-center">
          <AlertTriangle size={28} style={{ color: "#dc2626" }} className="mx-auto mb-3" />
          <h1 className="text-xl font-bold mb-2" style={{ color: "var(--px-ink)" }}>Missing reset token</h1>
          <p className="text-[14px]" style={{ color: "var(--px-ink-mute)" }}>
            This page only works when opened from a password-reset email. Redirecting to sign-in…
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 sm:p-6"
         style={{ background: "var(--px-bg)" }} data-testid="reset-password-page">
      <div className="w-full max-w-md">
        {fromSecureLock && (
          <div className="lms-card p-4 mb-4 flex items-start gap-3" data-testid="secure-lock-banner"
               style={{ borderColor: "var(--px-success)" }}>
            <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                 style={{ background: "var(--px-success-soft)", color: "var(--px-success)" }}>
              <ShieldCheck size={16} />
            </div>
            <div className="min-w-0">
              <div className="text-[13px] font-bold mb-0.5" style={{ color: "var(--px-ink)" }}>Account locked. Set a new password.</div>
              <div className="text-[12px]" style={{ color: "var(--px-ink-mute)" }}>
                We've signed out every device on your account. Choose a fresh password below to regain access.
              </div>
            </div>
          </div>
        )}

        <div className="lms-card p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-2">
            <Lock size={16} style={{ color: "var(--accent-deep)" }} />
            <span className="portal-kicker">— Reset password</span>
          </div>
          <h1 className="text-[24px] font-bold leading-tight mb-1" style={{ color: "var(--px-ink)" }}>
            Choose a new password
          </h1>
          <p className="text-[13.5px] mb-5" style={{ color: "var(--px-ink-mute)" }}>
            Minimum 8 characters. Use something only you would know.
          </p>

          {done ? (
            <div className="rounded-lg p-4 text-center" data-testid="reset-success"
                 style={{ background: "var(--px-success-soft)", border: "1px solid var(--px-success)" }}>
              <ShieldCheck size={22} className="mx-auto mb-2" style={{ color: "var(--px-success)" }} />
              <div className="text-[14px] font-semibold" style={{ color: "var(--px-ink)" }}>Password updated</div>
              <div className="text-[12px] mt-1" style={{ color: "var(--px-ink-mute)" }}>Taking you to sign in…</div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="lms-label">New password</label>
                <div className="relative">
                  <input
                    type={show ? "text" : "password"}
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    autoFocus
                    autoComplete="new-password"
                    className="lms-input pr-10"
                    placeholder="At least 8 characters"
                    data-testid="reset-new-password"
                  />
                  <button type="button" onClick={() => setShow((s) => !s)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 p-1"
                          style={{ color: "var(--px-ink-mute)" }} aria-label="Toggle password visibility">
                    {show ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="lms-label">Confirm new password</label>
                <input
                  type={show ? "text" : "password"}
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  autoComplete="new-password"
                  className="lms-input"
                  placeholder="Type the password again"
                  data-testid="reset-new-password-confirm"
                />
              </div>
              {err && (
                <div className="rounded-lg p-3 text-[12.5px] flex items-start gap-2"
                     style={{ background: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca" }}
                     data-testid="reset-error">
                  <AlertTriangle size={13} className="shrink-0 mt-0.5" />
                  <span>{err}</span>
                </div>
              )}
              <button type="submit"
                      disabled={busy}
                      className="w-full press-btn focus-ring rounded-full py-3 text-[14px] font-semibold disabled:opacity-50"
                      style={{ background: "var(--accent)", color: "white" }}
                      data-testid="reset-submit">
                {busy ? "Updating…" : "Update password"}
              </button>
              <div className="text-center pt-2">
                <Link to="/login" className="text-[12px]" style={{ color: "var(--accent-deep)" }}>
                  Back to sign-in
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
